import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { getTransactions, TransactionWithDetails } from '../lib/services/TransactionService';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Enregistrer les composants Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MonthlyData {
  month: string;
  depenses: number;
  revenus: number;
  balance: number;
}

const AnnualCharts: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [excludeTransfers, setExcludeTransfers] = useState(false); // Case à cocher "Ignorer les transferts" (décochée par défaut)

  // Noms des mois en français
  const monthNames = [
    'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
    'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'
  ];

  // Couleurs pour les courbes
  const chartColors = {
    depenses: '#FF5252',      // Rouge pour les dépenses
    revenus: '#4CAF50',       // Vert pour les revenus
    balance: '#2196F3'        // Bleu pour la balance
  };

  // Charger les données des transactions pour l'année sélectionnée
  const loadAnnualData = useCallback(async (year: number, excludeTransfersFlag = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;
      
      // Utiliser le service de transactions existant
      const { data: transactions, error } = await getTransactions(startDate, endDate);
      
      if (error) {
        console.error('Erreur lors du chargement des transactions:', error);
        setError('Erreur lors du chargement des données');
        return;
      }

      if (transactions) {
        // Filtrer les transferts si demandé
        const transactionsFiltered = excludeTransfersFlag
          ? transactions.filter(transaction => {
              // Exclure les transactions de type "transfert"
              if (transaction.type === 'transfert') {
                return false;
              }
              // Exclure les transactions dont la catégorie contient "transfert" dans son nom
              const categorieNom = transaction.categorie?.nom?.toLowerCase() || '';
              if (categorieNom.includes('transfert')) {
                return false;
              }
              return true;
            })
          : transactions;

        // Initialiser les données mensuelles
        const monthlyStats: MonthlyData[] = monthNames.map((month, _index) => ({
          month,
          depenses: 0,
          revenus: 0,
          balance: 0
        }));

        // Traiter chaque transaction (sans les transferts)
        transactionsFiltered.forEach((transaction: TransactionWithDetails) => {
          const transactionDate = new Date(transaction.date);
          const monthIndex = transactionDate.getMonth();
          
          if (monthIndex >= 0 && monthIndex < monthlyStats.length && transaction.type) {
            const monthData = monthlyStats[monthIndex]!;
            if (transaction.type === 'depense') {
              monthData.depenses += Math.abs(transaction.montant);
            } else if (transaction.type === 'revenu') {
              monthData.revenus += Math.abs(transaction.montant);
            }
          }
        });

        // Calculer la balance pour chaque mois
        monthlyStats.forEach(month => {
          month.balance = month.revenus - month.depenses;
        });

        setMonthlyData(monthlyStats);
      }
    } catch (error) {
      console.error('Exception lors du chargement des données:', error);
      setError('Une erreur est survenue lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les données au montage et quand l'année ou excludeTransfers change
  useEffect(() => {
    loadAnnualData(selectedYear, excludeTransfers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, excludeTransfers]);

  // Changer l'année
  const changeYear = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setSelectedYear(prev => prev - 1);
    } else {
      setSelectedYear(prev => prev + 1);
    }
  };

  // Préparer les données pour le graphique
  const chartData = {
    labels: monthlyData.map(m => m.month),
    datasets: [
      {
        label: 'Dépenses',
        data: monthlyData.map(m => m.depenses),
        borderColor: chartColors.depenses,
        backgroundColor: `${chartColors.depenses}20`,
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: chartColors.depenses,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
      {
        label: 'Revenus',
        data: monthlyData.map(m => m.revenus),
        borderColor: chartColors.revenus,
        backgroundColor: `${chartColors.revenus}20`,
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: chartColors.revenus,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
      {
        label: 'Balance',
        data: monthlyData.map(m => m.balance),
        borderColor: chartColors.balance,
        backgroundColor: `${chartColors.balance}20`,
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: chartColors.balance,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      }
    ]
  };

  // Configuration du graphique
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#fff',
          font: {
            size: 12,
            weight: 'bold' as const
          },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: '#23262F',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#444',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(tooltipItem: any) {
            const label = tooltipItem.dataset.label || '';
            const value = tooltipItem.parsed.y;
            return `${label}: ${formatAmount(value)}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: '#444',
          drawBorder: false
        },
        ticks: {
          color: '#fff',
          font: {
            size: 12
          }
        }
      },
      y: {
        grid: {
          color: '#444',
          drawBorder: false
        },
        ticks: {
          color: '#fff',
          font: {
            size: 12
          },
          callback: function(tickValue: string | number) {
            if (typeof tickValue === 'number') {
              return formatAmount(tickValue);
            }
            return tickValue;
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index' as const
    }
  };

  // Formater les montants pour l'affichage
  const formatAmount = (amount: number) => {
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}k€`;
    }
    return `${amount.toFixed(0)}€`;
  };

  // Calculer les totaux annuels
  const annualTotals = monthlyData.reduce((acc, month) => ({
    depenses: acc.depenses + month.depenses,
    revenus: acc.revenus + month.revenus,
    balance: acc.balance + month.balance
  }), { depenses: 0, revenus: 0, balance: 0 });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="text-white mt-4">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec navigation et année */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Graphiques Annuels</h2>
          <p className="text-dark-400">
            Analyse détaillée de vos finances par année
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Case à cocher "Ignorer les transferts" */}
          <div className="flex items-center space-x-2 bg-dark-800 rounded-lg px-4 py-2">
            <input
              type="checkbox"
              id="excludeTransfersAnnual"
              checked={excludeTransfers}
              onChange={(e) => setExcludeTransfers(e.target.checked)}
              className="w-4 h-4 rounded border-dark-700 bg-dark-800 text-primary-500 focus:ring-primary-500 focus:ring-2"
            />
            <label htmlFor="excludeTransfersAnnual" className="text-white cursor-pointer text-sm">
              Ignorer les transferts
            </label>
          </div>

          {/* Sélecteur d'année */}
          <div className="flex items-center space-x-4 bg-dark-800 rounded-lg p-2">
            <button
              onClick={() => changeYear('prev')}
              className="p-2 text-dark-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <span className="text-white font-bold text-lg px-4">
              {selectedYear}
            </span>
            
            <button
              onClick={() => changeYear('next')}
              className="p-2 text-dark-400 hover:text-white transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Gestion des erreurs */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{error}</p>
          <button
            onClick={() => loadAnnualData(selectedYear, excludeTransfers)}
            className="mt-2 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Résumé annuel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-red-500/10 border-red-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-dark-400 text-sm font-medium">Dépenses</p>
              <p className="text-2xl font-bold text-red-400 mt-1">
                {formatAmount(annualTotals.depenses)}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-500" />
            </div>
          </div>
        </div>

        <div className="card bg-green-500/10 border-green-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-dark-400 text-sm font-medium">Revenus</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {formatAmount(annualTotals.revenus)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
          </div>
        </div>

        <div className={`card ${annualTotals.balance >= 0 ? 'bg-blue-500/10 border-blue-500/20' : 'bg-orange-500/10 border-orange-500/20'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-dark-400 text-sm font-medium">Balance</p>
              <p className={`text-2xl font-bold mt-1 ${annualTotals.balance >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                {formatAmount(annualTotals.balance)}
              </p>
            </div>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${annualTotals.balance >= 0 ? 'bg-blue-500/10' : 'bg-orange-500/10'}`}>
              <DollarSign className={`w-6 h-6 ${annualTotals.balance >= 0 ? 'text-blue-500' : 'text-orange-500'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Graphique combiné des 3 courbes */}
      <div className="card">
        <h3 className="text-lg font-bold text-white mb-4 text-center">
          Évolution mensuelle - Dépenses, Revenus et Balance
        </h3>
        <div className="h-80">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* Tableau détaillé des mois */}
      <div className="card">
        <h3 className="text-lg font-bold text-white mb-4 text-center">
          Détail mensuel
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left py-3 px-4 text-dark-400 font-medium">Mois</th>
                <th className="text-right py-3 px-4 text-dark-400 font-medium">Dépenses</th>
                <th className="text-right py-3 px-4 text-dark-400 font-medium">Revenus</th>
                <th className="text-right py-3 px-4 text-dark-400 font-medium">Balance</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData.map((month, index) => (
                <tr key={index} className="border-b border-dark-800 hover:bg-dark-800/50 transition-colors">
                  <td className="py-3 px-4 text-white font-medium">{month.month}</td>
                  <td className="py-3 px-4 text-right text-red-400">
                    {formatAmount(month.depenses)}
                  </td>
                  <td className="py-3 px-4 text-right text-green-400">
                    {formatAmount(month.revenus)}
                  </td>
                  <td className={`py-3 px-4 text-right font-medium ${
                    month.balance >= 0 ? 'text-blue-400' : 'text-orange-400'
                  }`}>
                    {formatAmount(month.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnnualCharts; 